import { line } from "d3";
import { noop } from "lodash";
import React, { useMemo } from "react";

import {
  board,
  cone,
  flag,
  goal,
  hoop,
  ladder,
  net,
  pile,
  stickLeft,
  stickRight,
  tire,
  triangleStick,
} from "../../../images";
import { usePlayContext } from "../../../pages/Play/play-context";
import { Sport } from "../../../redux/slices";
import {
  BasketballMiscType,
  FrameStencils,
  HockeyMiscType,
  MarkerType,
  Play,
  Point,
  PointType,
  SoccerMiscType,
  Marker as TMarker,
} from "../../../types";
import { getSvgImageFilterColor, parseHtml } from "../../../utils";
import { usePlayingSurfaceContext } from "../../playing-surface/playing-surface-context";
import { StencilWrapper } from "../../stencil-wrapper";
import { getContrastColor } from "../utils";
import styles from "./marker.module.scss";

export const POSSESSION_RADIUS = 2.25;

interface BallMarkerProps {
  sport: Play["sport"];
  color: string;
}

const BallMarker = ({ sport, color }: BallMarkerProps): JSX.Element => {
  switch (sport) {
    case Sport.BASKETBALL:
      return <circle r={POSSESSION_RADIUS} stroke={color} className={styles.markerBall} />;
    case Sport.HOCKEY:
      return <circle r={0.75} cx={POSSESSION_RADIUS * 1.3} cy={POSSESSION_RADIUS * 0.8} fill={color} />;
    case Sport.SOCCER:
      return <circle r={0.75} cx={POSSESSION_RADIUS} cy={POSSESSION_RADIUS * 0.8} fill={color} />;
  }
};

const trapezoidLineGenerator = line<Point>()
  .x((d) => d.x)
  .y((d) => d.y);

export interface MarkerComponentBase {
  pos: Point;
  color: string | undefined;
}

/** ================================
 * Marker
 ================================ */
export type MarkerProps = {
  marker: TMarker;
  orientation: number;
  frameStencils: FrameStencils;
  isEditable?: boolean;
  handleEditText?: (id: string) => void;
};

export const Marker = (props: MarkerProps) => {
  const { marker, orientation, isEditable = false, handleEditText = noop } = props;
  const { markerType, pos, label = "", color } = marker;

  const { selectItemApi, animationTimerApi, drawToolbarApi, playStateApi } = usePlayContext();
  const clearItems = selectItemApi.clearItems;
  const { sport } = playStateApi.playState;

  const { effectiveTime } = animationTimerApi;
  const isTime0 = effectiveTime === 0;
  const isAnimationModeEnabled = isEditable && drawToolbarApi.animationView.isActive;

  const { editPlayerLabelApi } = usePlayingSurfaceContext();
  const { circleR, fontSize } = useMemo((): { circleR: number; fontSize: number } => {
    const circleR = sport === Sport.HOCKEY ? POSSESSION_RADIUS * 1.5 : POSSESSION_RADIUS;
    const circumference = 2 * Math.PI * circleR;
    const textLength = circumference - 0.25;
    const fontSize = label.length === 1 ? circleR * 2 : textLength / (label.length * 2.5);

    return { circleR, fontSize };
  }, [sport, label]);

  switch (markerType) {
    /** ======== PLAYER ======== */
    case MarkerType.PLAYER: {
      const { fill, possession } = marker;
      const { onEditPlayerLabel, isEditing, playerEditing } = editPlayerLabelApi;
      return (
        <StencilWrapper
          stencil={marker}
          selectionIconOffset={{ y: possession ? -1 : -0.5 }}
          onDoubleClick={(event) => {
            if (onEditPlayerLabel && isEditable) {
              clearItems();
              onEditPlayerLabel({ position: { x: event.clientX, y: event.clientY }, player: marker });
            }
          }}
          rotationCenter={{ x: 0, y: 1 }}
        >
          <g className={styles.markerPlayer} transform={`rotate(${-orientation})`}>
            {fill && <circle fill={color || "#000"} r={circleR} />}
            {isEditing && playerEditing?.id === marker.id ? null : (
              <>
                {fill ? (
                  <g>
                    <circle r={circleR} fill={color || "#000"} />
                    <text
                      fill={color ? getContrastColor(color) : "#fff"}
                      textAnchor="middle"
                      lengthAdjust="spacingAndGlyphs"
                      fontSize={fontSize}
                    >
                      {label || ""}
                    </text>
                  </g>
                ) : (
                  <text fill={color || "#000"} fontSize={4}>
                    {label || ""}
                  </text>
                )}
              </>
            )}
          </g>
          {possession && (!isAnimationModeEnabled || isTime0) && <BallMarker sport={sport} color={color || "#000"} />}
        </StencilWrapper>
      );
    }

    /** ======== BALL ======== */
    case MarkerType.BALL: {
      // get the color of the associated player
      const { frameStencils } = props;
      const associatedPlayer = frameStencils.find((stencil) => stencil.id === marker.associatedPlayer);
      const associatedPlayerColor = associatedPlayer?.color;

      return isAnimationModeEnabled && effectiveTime !== 0 ? (
        <StencilWrapper stencil={marker}>
          <BallMarker sport={sport} color={associatedPlayerColor || "#000"} />
        </StencilWrapper>
      ) : null;
    }

    case MarkerType.MISC: {
      switch (marker.miscType) {
        /** ======== HOOP ======== */
        case BasketballMiscType.HOOP: {
          const filter = color ? getSvgImageFilterColor(color) : undefined;

          return (
            <StencilWrapper stencil={marker} selectionIconOffset={{ y: -1 }} rotationCenter={{ x: 0, y: 1 }}>
              <image {...(filter ? { style: { filter } } : {})} href={hoop} width={5} x={-2.5} y={-1} />
            </StencilWrapper>
          );
        }

        /** ======== BALL_STATIC ======== */
        case BasketballMiscType.BALL_STATIC:
        case HockeyMiscType.PUCK_STATIC:
        case SoccerMiscType.BALL_STATIC:
          return (
            <StencilWrapper stencil={marker}>
              <circle r={0.65} fill={color || "#000"} />
            </StencilWrapper>
          );

        /** ======== FLAG ======== */
        case BasketballMiscType.FLAG:
        case HockeyMiscType.FLAG: {
          const filter = color ? getSvgImageFilterColor(color) : undefined;

          return (
            <StencilWrapper stencil={marker} rotationCenter={{ x: 0, y: 2.5 }}>
              <image {...(filter ? { style: { filter } } : {})} href={flag} width={2.5} x={-1.25} />
            </StencilWrapper>
          );
        }

        /** ======== CONE ======== */
        case BasketballMiscType.CONE:
        case HockeyMiscType.CONE: {
          const filter = color ? getSvgImageFilterColor(color) : undefined;

          return (
            <StencilWrapper stencil={marker} rotationCenter={{ x: 0, y: 3 }}>
              <image {...(filter ? { style: { filter } } : {})} href={cone} width={5} x={-2.5} />
            </StencilWrapper>
          );
        }

        /** ======== GOAL ======== */
        case SoccerMiscType.GOAL:
          return (
            <StencilWrapper stencil={marker} rotationCenter={{ x: 0, y: 1.5 }}>
              <image href={goal} x={-4} width={8} />
            </StencilWrapper>
          );

        /** ======== TIRE ========== */
        case HockeyMiscType.TIRE:
          return (
            <StencilWrapper stencil={marker}>
              <image href={tire} x={-3} width={6} />
            </StencilWrapper>
          );

        /** ======== STICKs ========= */
        case HockeyMiscType.STICK_RIGHT:
          return (
            <StencilWrapper stencil={marker}>
              <image href={stickRight} x={-3} width={6} />
            </StencilWrapper>
          );

        case HockeyMiscType.STICK_LEFT:
          return (
            <StencilWrapper stencil={marker}>
              <image href={stickLeft} x={-3} width={6} />
            </StencilWrapper>
          );

        /** ======== TRIANGLE STICK ======== */
        case HockeyMiscType.TRIANGLE_STICK:
          return (
            <StencilWrapper stencil={marker}>
              <image href={triangleStick} x={-3} width={6} />
            </StencilWrapper>
          );

        /** ======== BOARD ======== */
        case HockeyMiscType.BOARD:
          return (
            <StencilWrapper stencil={marker} rotationCenter={{ x: 1, y: 1 }}>
              <image href={board} x={-3} width={6} />
            </StencilWrapper>
          );

        /** ======== PUCK PILE ======== */
        case HockeyMiscType.PILE:
          return (
            <StencilWrapper stencil={marker}>
              <image href={pile} x={-2} width={4} />
            </StencilWrapper>
          );

        /** ======== NET ======== */
        case HockeyMiscType.NET:
          return (
            <StencilWrapper stencil={marker} rotationCenter={{ x: 1, y: 1 }} snapToAngleOnRotate>
              <image href={net} x={-3} width={6} />
            </StencilWrapper>
          );

        /** ======== LADDER ======== */
        case SoccerMiscType.LADDER:
          return (
            <StencilWrapper stencil={marker} rotationCenter={{ x: 0, y: 6 }}>
              <image href={ladder} x={-2} width={4} />
            </StencilWrapper>
          );

        default:
          return null;
      }
    }

    /** ======== TEXT ======== */
    case MarkerType.TEXT: {
      const textParsed = parseHtml(label || "");
      return (
        <StencilWrapper stencil={marker}>
          <foreignObject
            className={styles.text}
            width={marker.width}
            height={1}
            onDoubleClick={() => (isEditable ? handleEditText(marker.id) : noop)}
          >
            {textParsed}
          </foreignObject>
        </StencilWrapper>
      );
    }

    /** ======== SHADING RECTANGLE ======== */
    case MarkerType.SHADING_RECTANGLE: {
      const { width, height } = marker;

      return (
        <StencilWrapper
          stencil={marker}
          controlPoints={[
            { x: 0, y: 0, pointType: PointType.SHADE_VERTEX },
            { x: width, y: 0, pointType: PointType.SHADE_VERTEX },
            { x: width, y: height, pointType: PointType.SHADE_VERTEX },
            { x: 0, y: height, pointType: PointType.SHADE_VERTEX },
          ]}
        >
          {({ controlPoints }) => {
            const minX = Math.min(...controlPoints.map(({ x }) => x));
            const maxX = Math.max(...controlPoints.map(({ x }) => x));
            const minY = Math.min(...controlPoints.map(({ y }) => y));
            const maxY = Math.max(...controlPoints.map(({ y }) => y));

            return (
              <rect
                className={styles.shadingMarker}
                x={minX}
                y={minY}
                width={maxX - minX}
                height={maxY - minY}
                fill={color || "#ff0"}
              />
            );
          }}
        </StencilWrapper>
      );
    }

    /** ======== SHADING OVAL ======== */
    case MarkerType.SHADING_OVAL: {
      const { rx, ry } = marker;

      return (
        <g>
          <StencilWrapper
            stencil={marker}
            controlPoints={[
              { x: -rx, y: -ry, pointType: PointType.SHADE_VERTEX },
              { x: rx, y: -ry, pointType: PointType.SHADE_VERTEX },
              { x: rx, y: ry, pointType: PointType.SHADE_VERTEX },
              { x: -rx, y: ry, pointType: PointType.SHADE_VERTEX },
            ]}
          >
            {({ controlPoints }) => {
              const minX = Math.min(...controlPoints.map(({ x }) => x));
              const maxX = Math.max(...controlPoints.map(({ x }) => x));
              const minY = Math.min(...controlPoints.map(({ y }) => y));
              const maxY = Math.max(...controlPoints.map(({ y }) => y));

              const cx = (maxX + minX) / 2;
              const cy = (maxY + minY) / 2;
              const rx = (maxX - minX) / 2;
              const ry = (maxY - minY) / 2;

              return (
                <ellipse cx={cx} cy={cy} rx={rx} ry={ry} className={styles.shadingMarker} fill={color || "#ff0"} />
              );
            }}
          </StencilWrapper>
        </g>
      );
    }

    /** ======== SHADING TRAPEZOID ======== */
    case MarkerType.SHADING_TRAPEZOID: {
      const { shadeVertices } = marker;

      return (
        <StencilWrapper
          stencil={marker}
          controlPoints={shadeVertices.map((vertex) => ({
            x: vertex.x - pos.x,
            y: vertex.y - pos.y,
            pointType: PointType.SHADE_VERTEX,
          }))}
        >
          {({ controlPoints }) => (
            <path
              d={`${trapezoidLineGenerator(controlPoints)}Z`}
              className={styles.shadingMarker}
              fill={color || "#ff0"}
            />
          )}
        </StencilWrapper>
      );
    }

    default:
      return null;
  }
};
